import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { QrCodeScanner, CheckCircle, Cancel } from '@mui/icons-material';
import { attendanceAPI } from '../services/api';
// Assuming a react-qr-scanner library or similar is used in production.
// For now, we'll simulate a scanner with an input field for demonstration.

import { audioService } from '../utils/audioService';
import { getSocket } from '../services/socket';

export default function CheckInKiosk() {
  const [scanResult, setScanResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success' | 'error', message: '', userName: '' }

  // Listen for real-time attendance check-ins (e.g. from Biometrics or Mobile App)
  useEffect(() => {
    const socket = getSocket();
    if (socket) {
      const handleSocketCheckIn = (data) => {
        audioService.playAttendanceSuccessSound();
        setStatus({
          type: 'success',
          message: `Check-in recorded via ${data.source ? data.source.toUpperCase() : 'Biometric/QR'}`,
          userName: data.userName || 'Member'
        });
        setTimeout(() => setStatus(null), 4000);
      };

      socket.on('attendance:checkin', handleSocketCheckIn);
      return () => socket.off('attendance:checkin', handleSocketCheckIn);
    }
  }, []);

  // Simulate barcode/QR scanner input which acts like a keyboard typing and hitting Enter
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && scanResult) {
        handleScan(scanResult);
        setScanResult(''); // clear after scan
      } else if (e.key.length === 1) {
        setScanResult(prev => prev + e.key);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [scanResult]);

  const handleScan = async (data) => {
    if (!data) return;
    setLoading(true);
    setStatus(null);
    try {
      // Send token to the backend
      const res = await attendanceAPI.qrCheckIn(data);
      if (res.data.success) {
        audioService.playAttendanceSuccessSound();
        setStatus({
          type: 'success',
          message: res.data.message,
          userName: res.data.user?.name || 'Member'
        });
      }
    } catch (err) {
      audioService.playErrorSound();
      setStatus({
        type: 'error',
        message: err.response?.data?.message || 'Invalid or Expired QR Code',
        userName: ''
      });
    } finally {
      setLoading(false);
      // Clear status after 3 seconds
      setTimeout(() => setStatus(null), 3000);
    }
  };

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: '#0f172a',
      color: 'white',
      p: 4
    }}>
      <Paper elevation={24} sx={{
        p: 6,
        borderRadius: 4,
        textAlign: 'center',
        maxWidth: 600,
        width: '100%',
        bgcolor: '#1e293b',
        color: 'white'
      }}>
        <QrCodeScanner sx={{ fontSize: 80, color: '#38bdf8', mb: 2 }} />
        <Typography variant="h3" fontWeight={800} gutterBottom>
          Fitistic Kiosk
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 6 }}>
          Scan your QR Code to Check In/Out
        </Typography>

        <Box sx={{ minHeight: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {loading ? (
            <CircularProgress size={60} sx={{ color: '#38bdf8' }} />
          ) : status ? (
            <Box>
              {status.type === 'success' ? (
                <>
                  <CheckCircle sx={{ fontSize: 64, color: '#4ade80', mb: 2 }} />
                  <Typography variant="h4" fontWeight={700} color="#4ade80">
                    {status.userName}
                  </Typography>
                  <Typography variant="h6">{status.message}</Typography>
                </>
              ) : (
                <>
                  <Cancel sx={{ fontSize: 64, color: '#f87171', mb: 2 }} />
                  <Typography variant="h5" fontWeight={700} color="#f87171">
                    Access Denied
                  </Typography>
                  <Typography variant="h6">{status.message}</Typography>
                </>
              )}
            </Box>
          ) : (
            <Box sx={{
              border: '2px dashed #475569',
              borderRadius: 2,
              p: 4,
              width: '100%'
            }}>
              <Typography variant="body1" color="#94a3b8">
                Ready to scan...
              </Typography>
              <Typography variant="caption" color="#64748b" display="block" mt={1}>
                (Waiting for scanner input)
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
